import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import moment from 'moment';
import Input from '@/common/components/Input';
import SelectInput from '@/common/components/SelectInput';
import ActionButton from '@/common/components/buttons/ActionButton';
import Loading from '@/common/components/Loading';
import { Colors } from '@/common/colors/colors';
import { formatToDateTamplate } from '@/common/functions/dateFormater';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { getCallAnalysisReport } from '@/API/repositories/callAnalysis';
import { CONSULTANT_ROLES } from '@/pages/login/components/LoginForm/loginForm';
import {
	ButtonWrapper,
	ReportCard,
	ReportCards,
	Row,
	SearchBar,
	Wrapper,
} from '../callAnalysis/callAnalysis.styles';
import { formatScore } from '../callAnalysis/helpers/labels';

const CallAnalysisReport = () => {
	const [report, setReport] = useState(null);
	const [consultants, setConsultants] = useState([]);
	const [managers, setManagers] = useState([]);

	const { makeRequest, hasUnfilledRequest } = useRequestsContext();
	const {
		filterUsersByRoles,
		commonData: { users },
		options: { marketsOptions },
		queryValues: { isFetching },
	} = useCommonDataContext();

	const handleSearch = async (values) => {
		const payload = {
			date_from: moment(values.fromDate).startOf('day').toISOString(),
			date_to: moment(values.toDate).endOf('day').toISOString(),
			consultant: values.consultant?.value,
			team_leader: values.team_leader?.value,
			market: values.market?.value,
		};

		const response = await makeRequest(
			getCallAnalysisReport.bind(null, payload),
		);

		if (response?.data) {
			setReport(response.data);
		}
	};

	const formik = useFormik({
		initialValues: {
			fromDate: formatToDateTamplate(moment().subtract(30, 'days').toDate()),
			toDate: formatToDateTamplate(new Date()),
			consultant: null,
			team_leader: null,
			market: null,
		},
		onSubmit: handleSearch,
	});

	useEffect(() => {
		setConsultants(filterUsersByRoles(CONSULTANT_ROLES));
		setManagers(
			filterUsersByRoles(['CALL_CENTER_MANAGER', 'HEAD_CC_MANAGER']),
		);
		formik.submitForm();
	}, [users]);

	const overview = report?.overview || {};
	const consultantMap = Object.fromEntries(
		(consultants || []).map((c) => [c.value, c.label]),
	);

	return (
		<>
			{(hasUnfilledRequest(getCallAnalysisReport) || isFetching) && (
				<Loading />
			)}
			<Wrapper>
				<SearchBar>
					<Row>
						<Input
							type="date"
							name="From"
							id="fromDate"
							value={formik.values.fromDate}
							onChange={formik.handleChange}
							width={80}
							inputWidth={140}
							color={Colors.darkBlue}
						/>
						<Input
							type="date"
							name="To"
							id="toDate"
							value={formik.values.toDate}
							onChange={formik.handleChange}
							width={80}
							inputWidth={140}
							color={Colors.darkBlue}
						/>
						<SelectInput
							name="Consultant"
							selected={formik.values.consultant}
							setSelected={(value) =>
								formik.setFieldValue('consultant', value)
							}
							options={consultants}
							width={100}
							selectWidth={200}
							color={Colors.darkBlue}
						/>
						<SelectInput
							name="Team leader"
							selected={formik.values.team_leader}
							setSelected={(value) =>
								formik.setFieldValue('team_leader', value)
							}
							options={managers}
							width={100}
							selectWidth={200}
							color={Colors.darkBlue}
						/>
						<SelectInput
							name="Market"
							selected={formik.values.market}
							setSelected={(value) => formik.setFieldValue('market', value)}
							options={marketsOptions}
							width={100}
							selectWidth={200}
							color={Colors.darkBlue}
						/>
					</Row>
					<ButtonWrapper>
						<ActionButton defaultText="Search" onClick={formik.handleSubmit} />
					</ButtonWrapper>
				</SearchBar>

				{report && (
					<>
						<ReportCards>
							<ReportCard>
								<h4>Average score</h4>
								<p>{formatScore(overview.avg_score || 0)}</p>
							</ReportCard>
							<ReportCard>
								<h4>Total calls</h4>
								<p>{overview.total || 0}</p>
							</ReportCard>
							<ReportCard>
								<h4>Critical errors</h4>
								<p>{overview.critical_error_count || 0}</p>
							</ReportCard>
							<ReportCard>
								<h4>2nd language offer %</h4>
								<p>{report.second_language?.offer_rate_percent ?? 0}%</p>
							</ReportCard>
							<ReportCard>
								<h4>Successful upsell %</h4>
								<p>{report.second_language?.success_rate_percent ?? 0}%</p>
							</ReportCard>
						</ReportCards>

						{!!report.by_consultant?.length && (
							<div style={{ width: '100%', maxWidth: 1100 }}>
								<h3 style={{ color: Colors.darkBlue }}>
									Consultant comparison
								</h3>
								<table className="styled-table">
									<thead>
										<tr>
											<th>Consultant</th>
											<th>Avg score</th>
											<th>Calls</th>
											<th>Critical errors</th>
										</tr>
									</thead>
									<tbody>
										{report.by_consultant.map((row) => (
											<tr key={String(row._id)}>
												<td>
													{consultantMap[row._id] || String(row._id || '—')}
												</td>
												<td>{formatScore(row.avg_score)}</td>
												<td>{row.total}</td>
												<td>{row.critical_error_count}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}

						{!!report.most_missed_criteria?.length && (
							<div style={{ width: '100%', maxWidth: 1100 }}>
								<h3 style={{ color: Colors.darkBlue }}>
									Most missed criteria
								</h3>
								<table className="styled-table">
									<thead>
										<tr>
											<th>Criterion</th>
											<th>Count</th>
										</tr>
									</thead>
									<tbody>
										{report.most_missed_criteria.map((row) => (
											<tr key={row._id?.criterion_id || row._id?.label}>
												<td>
													{row._id?.label || row._id?.criterion_id || '—'}
												</td>
												<td>{row.count}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}

						{!!report.critical_errors?.length && (
							<div style={{ width: '100%', maxWidth: 1100 }}>
								<h3 style={{ color: Colors.darkBlue }}>Critical errors</h3>
								<table className="styled-table">
									<thead>
										<tr>
											<th>Error</th>
											<th>Count</th>
										</tr>
									</thead>
									<tbody>
										{report.critical_errors.map((row) => (
											<tr key={row._id}>
												<td>{row.label || row._id}</td>
												<td>{row.count}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}

						{!!report.score_over_time?.length && (
							<div style={{ width: '100%', maxWidth: 1100 }}>
								<h3 style={{ color: Colors.darkBlue }}>Score over time</h3>
								<table className="styled-table">
									<thead>
										<tr>
											<th>Date</th>
											<th>Avg score</th>
											<th>Calls</th>
										</tr>
									</thead>
									<tbody>
										{report.score_over_time.map((row) => (
											<tr key={row._id}>
												<td>{row._id}</td>
												<td>{formatScore(row.avg_score)}</td>
												<td>{row.total}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</Wrapper>
		</>
	);
};

export default CallAnalysisReport;
